import { computed, Ref, ref, watch } from '@vue/composition-api';

import { useAsyncResult, useAsyncState } from '@ligo/shared/utils';
import { NotificationService } from '@ligo/dashboard/customer/store';
import {
  Notification,
  notificationWithClickableOption
} from '@ligo/dashboard/store';
import { useRouter, useStore } from '../composables';
import { NavItem, LinkType, UserMenuItem } from '../models';
import { MIGRATION } from '@ligo/dashboard/customer/store';

export const TOKEN_FOR_SEPARATOR = '--';

export function useNavItems() {
  const store = useStore();
  const user = computed(() => store.getters['authentication/user']);
  const pendingBankingRequests = computed(() => {
    return user.value?.pending_banking_requests || 0;
  });

  const navItems: Ref<NavItem[]> = computed(() => [
    {
      route: 'Home',
      icon: 'fas fa-grid-2',
      title: 'nav.home',
      subtitle: ''
    },
    {
      route: 'Reminders',
      icon: 'fas fa-list-check',
      title: 'nav.contract_reminders',
      subtitle: 'contract.reminders.no_items'
    },
    {
      route: 'Incorporations',
      icon: 'fas fa-building',
      title: 'nav.incorporations.title',
      subtitle: 'nav.incorporations.subtitle'
    },
    {
      route: 'Modifications',
      icon: 'fas fa-rotate',
      title: 'nav.modifications.title',
      subtitle: 'nav.modifications.subtitle'
    },
    {
      route: 'ActiveContracts',
      icon: 'fas fa-file-contract',
      title: 'nav.contracts_title',
      subtitle: 'nav.contracts_subtitle',
      closed: ref(true),
      disabled_cta: '',
      children: [
        {
          route: 'DocumentsLibrary',
          title: 'nav.documents_library',
          icon: 'far fa-file-spreadsheet'
        },
        {
          route: 'SignatureHome',
          title: 'nav.signature_home',
          icon: 'far fa-signature',
          newItem: true
        }
      ]
    },
    {
      route: 'DgaServices',
      icon: 'fas fa-user',
      title: 'nav.dga_services',
      closed: ref(true),
      disabled_cta: ''
    },
    {
      route: 'TaxServices',
      icon: 'fas fa-calculator',
      title: 'nav.tax_services',
      closed: ref(true),
      disabled_cta: ''
    },
    {
      route: 'Banking',
      icon: 'fas fa-landmark',
      title: 'nav.banking',
      badgeNumber: pendingBankingRequests.value,
      disabled_cta: ''
    },
    {
      route: 'Hosting',
      icon: 'fas fa-globe',
      title: 'nav.hosting',
      disabled_cta: ''
    },
    {
      route: 'Lawyers',
      icon: 'fas fa-scale-balanced',
      title: 'nav.lawyers',
      subtitle: 'nav.lawyers_subtitle',
      redirect_to: MIGRATION.SPECIALIST
    }
  ]);

  return { navItems };
}

export function useUserMenuItems() {
  const menuItems = computed(() => {
    const items: UserMenuItem[] = [
      {
        title: 'nav.settings',
        icon: 'far fa-user',
        link: MIGRATION.ACCOUNT,
        type: LinkType.Route
      },
      {
        title: 'nav.account',
        icon: 'far fa-credit-card',
        link: MIGRATION.INVOICES,
        type: LinkType.Route
      },
      {
        title: 'nav.help',
        icon: 'far fa-comment-question',
        link: '',
        type: LinkType.OpenChat
      },
      { title: TOKEN_FOR_SEPARATOR },
      {
        title: 'nav.logout',
        icon: 'far fa-right-from-bracket',
        type: LinkType.Logout
      }
    ];

    return items;
  });

  const logout = menuItems.value.slice(-1)[0];

  return { menuItems, logout };
}

export function useNotifications() {
  const router = useRouter();
  const page = ref(1);
  let done = undefined;
  const items = ref([]);

  const { state, loading, last, load } = useAsyncResult(
    () => NotificationService.list({ page: page.value }),
    [page]
  );

  const { state: unseenCount, load: loadUnseen } = useAsyncState(
    () =>
      NotificationService.list({ unseen: true }).then((response) => {
        return parseInt(response.headers['x-total']);
      }),
    0
  );

  function onMarkAsSeen(ids: string[]) {
    loading.value = true;
    NotificationService.markAsSeen(ids).then(() => {
      page.value = 1;
      loadUnseen();
      load();
    });
  }

  function onScroll(event: () => void) {
    if (last.value) return;
    if (!done) done = event;
    page.value++;
  }

  function onSeeNotification(notification: Notification) {
    const npNotableType = 'NotaryProduct';
    const contractNotableType = 'Contract';
    const membershipType = 'Membership';

    if (!notification.seen_at) {
      onMarkAsSeen([notification.id]);
    }
    if (notification.notable_type == npNotableType)
      onNavigateToProduct(notification);
    else if (notification.notable_type == contractNotableType)
      onNavigateToContract(notification);
    else if (notification.notable_type == membershipType) goToAccount();
  }

  function goToAccount() {
    router.push({
      name: 'Account'
    });
  }

  function onNavigateToContract(notification: Notification) {
    router.push({
      name: 'ContractPreview',
      params: { uuid: notification.notable_uuid }
    });
  }

  function onNavigateToProduct(notification: Notification): void {
    if (
      router.currentRoute.name != 'NotaryProductDetail' ||
      router.currentRoute.params?.uuid != notification.notable_uuid
    ) {
      router.push({
        name: 'NotaryProductDetail',
        params: { uuid: notification.notable_uuid }
      });
    }
  }

  watch(state, (newItems) => {
    if (page.value > 1) {
      items.value = [...items.value, ...newItems];
    } else {
      items.value = newItems;
    }
    done && done();
  });

  const notifications = computed(() =>
    items.value.map(notificationWithClickableOption)
  );

  return {
    items: notifications,
    unseenCount,
    loadingNotifications: loading,
    lastNotifications: last,
    onScroll,
    onMarkAsSeen,
    onSeeNotification
  };
}
